import React, { useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";
import { History, Pencil, Trash2 } from "lucide-react";

// Interface
import { type MultiRunSequence, type MultiSetProp } from "../common/commonV4Interface";
import { AlertV4, ButtonV4, ChipV4, FieldV4, ModalV4, SelectV4 } from "../common/ui";
import { BLC_STATUS, type PickMainState } from "react-lib/apps/HISV3/common/CommonInterface";
import { type OnEventHandler } from "react-lib/appcon/common/SearchBoxDropdown";
import { formatDate, formatISOtoBEDateTime } from "react-lib/utils/dateUtils";
import formatComma from "react-lib/utils/formatComma";
import {
  ACTIONS,
  BTN_ACTS,
  ORDER_STATUS,
  SEQUENCE_NAME,
  type EncounterType,
  type RunSequence,
  type SetProp,
  type State,
} from "../common/sequence/PatientOrdersV4";
import {
  ACTIONS as SHEET_ACTIONS,
  BTN_ACTS as SHEET_BTN_ACTS,
  EDITABLE_KINDS,
  SEQUENCE_NAME as SHEET_SEQUENCE,
  SHEET_KINDS,
  type AdrChoice,
  type EditableKind,
  type OrderMasterOptions,
  type SaveSetResult,
  type SetEntry,
  type SheetKind,
  type SheetLine as SheetDraftLine,
  type State as OrderSheetState,
} from "../common/sequence/OrderSheetV4";
import {
  ACTIONS as APPOINTMENT_ACTIONS,
  SEQUENCE_NAME as APPOINTMENT_SEQUENCE,
  type State as OrderAppointmentState,
} from "../common/sequence/OrderAppointmentV4";
import { errorTextOf } from "../common/errorTextV4";
import {
  CATEGORY_LABELS,
  CATEGORY_TONES,
  ORDER_DEF,
  SHEET_CATEGORIES,
  SEVERITY_TEXT,
  URGENCY_LABELS,
  allocatePackages,
  cancelKeyOf,
  codeOf,
  createdTimeOf,
  formatLabSummary,
  formatPackage,
  formatPickPrice,
  formatTat,
  labDestinationOptions,
  formatTime,
  getCategory,
  getLogRows,
  getReviewItems,
  getStep,
  isCapsuleDrug,
  isLogKind,
  isPendingLine,
  parseStatus,
  pickOfLine,
  priceOf,
  sheetGroupOf,
  type DoneRow,
  type LabTest,
  type PickItem,
  type SheetCategory,
  type SheetRow,
} from "./ordersModelV4";
import { stripHtml } from "./resultModelV4";
import CardOrderAppointmentV4 from "./CardOrderAppointmentV4";
import { providerIdsOf } from "../common/oaProvidersV4";
import { useOAProvidersV4 } from "../common/useOAProvidersV4";
import {
  SheetLineControls,
  SheetLineFields,
  isCostCenter,
  sheetAmount,
  sheetMissing,
  sheetMode,
  sheetSummary,
} from "./OrderSheetLineV4";

import { type State as OrderCentralLabState } from "react-lib/apps/HISV3/LAB/sequence/OrderCentralLab";
import { type OrderSetTemplateSequence } from "react-lib/apps/HISV3/common/sequence/OrderSetTemplate";

// Types
type CardOrdersV4Props = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "searchedItemListWithKey"
> & {
  masterOptions?: OrderMasterOptions;
  onEvent?: OnEventHandler;
  setProp?: MultiSetProp<SetProp, "OrderCentralLabSequence" | "OrderSheetV4Sequence">;
  runSequence: MultiRunSequence<
    RunSequence,
    | typeof LAB_SEQUENCE
    | typeof ORDER_SET_SEQUENCE
    | typeof SHEET_SEQUENCE
    | typeof APPOINTMENT_SEQUENCE
  >;
  OrderCentralLabSequence?: OrderCentralLabState["OrderCentralLabSequence"];
  OrderSetTemplateSequence?: OrderSetTemplateSequence;
  OrderSheetV4Sequence?: OrderSheetState["OrderSheetV4Sequence"];
  OrderAppointmentV4Sequence?: OrderAppointmentState["OrderAppointmentV4Sequence"];
  PatientOrdersV4Sequence?: State["PatientOrdersV4Sequence"];
  doctorId?: number;
  emrId?: number;
  encounter: EncounterType;
  encounterNumber: string;
  patient: { hn: string; name: string; phone: string };
  divisionName: string;
  apiToken?: string;
  divisionId?: number;
  isDoctor?: boolean;
  onGoAppointments?: () => void;
  onAppointmentChanged?: () => void;
};

type Panel = "" | "sets" | "favs";

type SearchTarget = "sheet" | "advance";

type SetTab = "all" | "mine" | "clinic";

type SaveScope = Exclude<SetTab, "all">;

// Const
const FEE_STATUS = "Ordered";

const LAB_SEQUENCE = "OrderCentralLab";

const LAB_CARD_KEY = "CardOrdersV4Lab";

const ORDER_SET_SEQUENCE = "OrderSetTemplate";

const ORDER_SET_CARD_KEY = "CardOrdersV4OrderSet";

const MAX_RESULTS = 6;

const MIN_QUERY = 3;

const SET_TABS: [SetTab, string][] = [
  ["all", "ทั้งหมด"],
  ["mine", "ของฉัน"],
  ["clinic", "ของคลินิก"],
];

const FAV_PLACEHOLDER = "+ เพิ่มรายการโปรด…";

const CardOrdersV4 = (props: CardOrdersV4Props) => {
  const [openGroups, setOpenGroups] = useState<Partial<Record<SheetCategory, boolean>>>({});
  const [cancelRow, setCancelRow] = useState<DoneRow | null>(null);
  const [cancelNote, setCancelNote] = useState("");
  const [cancelPassword, setCancelPassword] = useState("");
  const [logRow, setLogRow] = useState<DoneRow | null>(null);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<SheetCategory | "all">("all");
  const [flash, setFlash] = useState("");
  const [notice, setNotice] = useState<{
    text: string;
    key?: string;
    category?: SheetCategory;
  } | null>(null);
  const [openLines, setOpenLines] = useState<Record<string, boolean>>({});
  const [openKids, setOpenKids] = useState<Record<string, boolean>>({});
  const [labTestInfo, setLabTestInfo] = useState<Record<string, LabTest>>({});
  const [pendingCode, setPendingCode] = useState("");
  const [panel, setPanel] = useState<Panel>("");
  const [adrReason, setAdrReason] = useState("");
  const [interactionReason, setInteractionReason] = useState("");
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [setTab, setSetTab] = useState<SetTab>("all");
  const [openSetId, setOpenSetId] = useState<number | null>(null);
  const [setExcluded, setSetExcluded] = useState<Record<string, boolean>>({});
  const [setRemoved, setSetRemoved] = useState<Record<number, string[]>>({});
  const [saveSet, setSaveSet] = useState<{ open: boolean; name: string; scope: SaveScope }>({
    open: false,
    name: "",
    scope: "mine",
  });
  const [favourites, setFavourites] = useState<PickItem[]>([]);
  const [searchTarget, setSearchTarget] = useState<SearchTarget>("sheet");

  const sheetKeysRef = useRef<string[]>([]);
  const icd9TimerRef = useRef<ReturnType<typeof setTimeout>>();
  const setFlashRef = useRef("");
  const confirmRef = useRef<{
    keys: string[];
    edited: number;
    cancelled: number;
    pkgCount: number;
  } | null>(null);

  // Effect
  useEffect(() => {
    props.runSequence({
      sequence: SEQUENCE_NAME,
      encounter: props.encounter,
      restart: true,
    });

    props.runSequence({ sequence: SHEET_SEQUENCE, restart: true, encounterId: props.encounter.id });

    props.runSequence({
      sequence: ORDER_SET_SEQUENCE,
      restart: true,
      cardKey: ORDER_SET_CARD_KEY,
    });
  }, [props.encounter.id]);

  useEffect(() => {
    // OrderCentralLab ต้องมี selectedEmr ก่อนเริ่ม
    if (props.emrId) {
      props.runSequence({ sequence: LAB_SEQUENCE, restart: true });
    }
  }, [props.encounter.id, props.emrId]);

  useEffect(() => {
    const ids = (props.OrderSetTemplateSequence?.templateList ?? []).map((item: any) => item.id);

    if (panel === "sets" && ids.length > 0) {
      props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.LOAD_SETS, ids });
    }
  }, [panel, props.OrderSetTemplateSequence?.templateList]);

  useEffect(() => {
    // ล้างเฉพาะเมื่อมีค่าค้าง (ตอนเปิดหน้าไม่แตะ OrderCentralLabSequence)
    if (query || categoryFilter !== "all") {
      handleClearSearch();
    }
  }, [panel]);

  useEffect(() => {
    if (query.trim().length < MIN_QUERY) {
      return;
    }

    const kinds = SHEET_KINDS.filter((kind) => [kind, "all"].includes(categoryFilter));

    const timer = setTimeout(() => {
      if (["all", "lab"].includes(categoryFilter)) {
        props.runSequence({ sequence: LAB_SEQUENCE, action: "search", cardKey: LAB_CARD_KEY });
      }

      if (kinds.length > 0) {
        props.runSequence({
          sequence: SHEET_SEQUENCE,
          action: SHEET_ACTIONS.SEARCH,
          query,
          kinds,
        });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [query, categoryFilter]);

  useEffect(() => {
    const dueAppointments = props.OrderAppointmentV4Sequence?.dueAppointments ?? [];

    if (dueAppointments.length === 0 || !props.emrId) {
      return;
    }

    const orders = dueAppointments.flatMap((item) => (item.extra?.orders ?? []) as SetEntry[]);

    addEntries(
      orders,
      `ดึงคำสั่งล่วงหน้าจากนัด ${dueAppointments.map((item) => item.id).join(", ")}`,
    );
    props.runSequence({
      sequence: APPOINTMENT_SEQUENCE,
      action: APPOINTMENT_ACTIONS.MARK_PULLED,
      ids: dueAppointments.map((item) => Number(item.id)),
    });
  }, [props.OrderAppointmentV4Sequence?.dueAppointments, props.emrId]);

  // Memo
  const sequence = props.PatientOrdersV4Sequence;

  const draftItems: any[] = props.OrderCentralLabSequence?.labOrder?.order_items ?? [];

  const labEditId: number | undefined = props.OrderCentralLabSequence?.labOrder?.id;

  const draftKey = JSON.stringify(draftItems);

  const sheetLines: SheetDraftLine[] = props.OrderSheetV4Sequence?.lines ?? [];

  const sheetEditing = props.OrderSheetV4Sequence?.editing ?? [];

  const editingOrderIds = useMemo(
    () =>
      new Set([
        ...sheetEditing.filter((item) => item.kind !== "df").map((item) => item.orderId),
        labEditId,
      ]),
    [sheetEditing, labEditId],
  );

  const editingFeeIds = useMemo(
    () => new Set(sheetEditing.filter((item) => item.kind === "df").map((item) => item.orderId)),
    [sheetEditing],
  );

  const doctorOptions = props.OrderSheetV4Sequence?.doctorOptions ?? [];
  const providers = useOAProvidersV4(props.apiToken, props.divisionId);
  const eligibleDoctorOptions =
    props.apiToken === undefined
      ? doctorOptions
      : providers.doctors
          .filter((doctor) => doctor.employee)
          .map((doctor) => ({
            id: doctor.id,
            employee: doctor.employee!,
            name: doctor.full_name || doctor.name_code || "",
            providers: doctor.provider ?? [],
          }));
  const eligibleProviderIds =
    props.apiToken === undefined ? undefined : providerIdsOf(providers.doctors);
  const invalidDF = sheetLines.some(
    (line) =>
      line.kind === "df" &&
      !eligibleDoctorOptions.some((doctor) => String(doctor.employee) === line.fields.employee),
  );

  const lastConfirmed = sequence?.lastConfirmed;

  const lastOrderBy = doctorOptions.find((doctor) => doctor.id === lastConfirmed?.by)?.name;

  const savedText = lastConfirmed
    ? `ยืนยันคำสั่งล่าสุด ${formatTime(lastConfirmed.time)}${lastOrderBy ? ` โดย ${lastOrderBy}` : ""}`
    : "ยังไม่ได้ยืนยันคำสั่ง";

  const packages = props.OrderSheetV4Sequence?.packages ?? [];

  const pkgUse = useMemo(
    () =>
      allocatePackages(
        [
          ...draftItems.map((item) => ({
            key: `draft_${item.lab_code}`,
            product: Number(item.product) || null,
            quantity: 1,
          })),
          ...sheetLines.map((line) => ({
            key: line.key,
            product: line.product,
            quantity: line.quantity,
          })),
        ],
        packages,
      ),
    [draftKey, sheetLines, packages],
  );

  const sheetErrors = props.OrderSheetV4Sequence?.errors ?? {};

  useEffect(() => {
    const keys = sheetLines.map((line) => line.key);
    const added = keys.filter((key) => !sheetKeysRef.current.includes(key));

    sheetKeysRef.current = keys;

    if (added.length === 0) {
      return;
    }

    confirmRef.current = null;

    if (setFlashRef.current) {
      const missing = sheetLines.filter(
        (line) => added.includes(line.key) && sheetMissing(line).length > 0,
      );

      setOpenLines((lines) => ({
        ...lines,
        ...Object.fromEntries(missing.map((line) => [line.key, true])),
      }));
      setFlash(
        missing.length > 0
          ? `${setFlashRef.current} · เปิดการ์ดรายละเอียดให้กรอกแล้ว`
          : setFlashRef.current,
      );
      setFlashRef.current = "";

      return;
    }

    setOpenLines((lines) => ({ ...lines, ...Object.fromEntries(added.map((key) => [key, true])) }));

    const newCount = sheetLines.filter((line) => added.includes(line.key) && !line.orderId).length;

    if (newCount > 0) {
      setFlash(`เพิ่ม ${newCount} รายการเข้าใบคำสั่งแล้ว · เปิดการ์ดรายละเอียดให้กรอกแล้ว`);
    }
  }, [sheetLines]);

  useEffect(() => {
    if (!pendingCode || !draftItems.some((item) => item.lab_code === pendingCode)) {
      return;
    }

    setOpenLines({ ...openLines, [`draft_${pendingCode}`]: true });
    setFlash("เพิ่ม 1 รายการเข้าใบคำสั่งแล้ว · เปิดการ์ดรายละเอียดให้กรอกแล้ว");
    setPendingCode("");
  }, [draftKey, pendingCode]);

  const labOrders = useMemo(
    () => new Map((sequence?.labOrderList ?? []).map((item) => [item.id, item])),
    [sequence?.labOrderList],
  );

  const queueOrders = useMemo(
    () => new Map((sequence?.encounter?.doctor_orders ?? []).map((item) => [item.id, item])),
    [sequence?.encounter?.doctor_orders],
  );

  const orderRows = useMemo(
    () =>
      (sequence?.orderList ?? []).map((order) => {
        const category = getCategory(order);
        const labOrder = category === "lab" ? labOrders.get(order.id) : undefined;
        const queueOrder = queueOrders.get(order.id);
        const steps = category ? ORDER_DEF[category][1] : [];
        const isCancelled = order.order_status === ORDER_STATUS.CANCEL;
        const step = category
          ? getStep(category, order, parseStatus(queueOrder?.order_summary_detail_cache))
          : 0;
        const isLast = steps.length > 0 && step === steps.length - 1;
        const items = labOrder?.order_items?.length
          ? labOrder.order_items.map((item: any) => item.name as string)
          : [stripHtml(order.detail)];

        return {
          order,
          category,
          labOrder,
          queueOrder,
          isCancelled,
          isLocked: !isCancelled && isLast,
          canEdit:
            !isCancelled &&
            step === 0 &&
            !!order.can_edit &&
            (category === "lab" || EDITABLE_KINDS.includes(category as EditableKind)),
          canCancel: !isCancelled && !isLast && !!order.can_cancel,
          status: isCancelled ? "Cancelled" : (steps[step] ?? order.order_status_display),
          items,
        };
      }),
    [sequence?.orderList, labOrders, queueOrders],
  );

  const groups = useMemo(() => {
    const lines = new Map<SheetCategory, SheetRow[]>();

    const push = (category: SheetCategory, line: SheetRow) => {
      lines.set(category, [...(lines.get(category) ?? []), line]);
    };

    for (const row of orderRows) {
      if (row.isCancelled || !row.category || editingOrderIds.has(row.order.id)) {
        continue;
      }

      const prices: any[] = row.order.price?.orders ?? [];
      const labItems: any[] = row.labOrder?.order_items ?? [];

      // ราคาต่อรายการของแลปเรียงตามลำดับ order_items
      if (labItems.length > 0 && labItems.length === prices.length) {
        for (const [index, item] of labItems.entries()) {
          push("lab", {
            key: `${row.order.id}_${item.id}`,
            name: item.name,
            outLab: item.lab_type_label === "Out lab",
            summary: URGENCY_LABELS[item.urgency] ?? item.urgency,
            amount: Number(prices[index]?.price_total || 0),
          });
        }
      } else {
        push(row.category, {
          key: `${row.order.id}`,
          name: stripHtml(row.order.detail),
          amount: Number(row.order.price?.price_total || 0),
        });
      }
    }

    for (const [index, item] of draftItems.entries()) {
      const key = `draft_${item.lab_code}`;
      const amount = Number(item.price_total || 0);

      push("lab", {
        key,
        name: item.name,
        outLab: item.lab_type_label === "Out lab",
        summary: formatLabSummary(item, labTestInfo[item.lab_code]),
        amount: pkgUse.has(key) ? 0 : amount,
        draftIndex: index,
        pkgText: formatPackage(pkgUse.get(key), amount),
      });
    }

    for (const line of sheetLines) {
      push(line.kind, {
        key: line.key,
        name: line.name,
        summary: sheetSummary(line, doctorOptions, props.masterOptions),
        amount: pkgUse.has(line.key) ? 0 : sheetAmount(line),
        pkgText: formatPackage(pkgUse.get(line.key), line.priceUnit),
        adr: line.raw.adr
          ? { adversary: line.raw.adr.adversary, reason: line.fields.adrNote ?? "" }
          : undefined,
        doseWarning: line.raw.doseWarning,
        draft: line,
        missing: sheetMissing(line),
      });
    }

    for (const item of sequence?.doctorFeeList ?? []) {
      if (editingFeeIds.has(Number(item.id))) {
        continue;
      }

      push("df", {
        key: `df_${item.id}`,
        name: item.doctor_fee_rule_display ?? "",
        summary: item.received_employee_display
          ? `แพทย์ผู้รับ ${item.received_employee_display}`
          : "",
        amount: Number(item.price_total || 0),
      });
    }

    for (const item of sequence?.miscList ?? []) {
      push("misc", {
        key: `misc_${item.id}`,
        name: item.name ?? "",
        summary: item.note ?? "",
        amount: Number(item.price_total || 0),
      });
    }

    return SHEET_CATEGORIES.filter((category) => lines.has(category)).map((category) =>
      sheetGroupOf(category, lines.get(category) ?? []),
    );
  }, [
    orderRows,
    draftKey,
    editingOrderIds,
    editingFeeIds,
    labTestInfo,
    sheetLines,
    doctorOptions,
    props.masterOptions,
    sequence?.doctorFeeList,
    sequence?.miscList,
    pkgUse,
  ]);

  const pendingGroups = groups
    .map((group) => sheetGroupOf(group.category, group.lines.filter(isPendingLine)))
    .filter((group) => group.lines.length > 0);
  const pendingCount = pendingGroups.reduce((sum, group) => sum + group.lines.length, 0);
  const pendingTotal = pendingGroups.reduce((sum, group) => sum + group.subtotal, 0);

  const doneRows: DoneRow[] = [
    ...orderRows.flatMap((row) =>
      row.category
        ? [
            {
              ...row,
              key: `order_${row.order.id}`,
              category: row.category,
              isEditing: editingOrderIds.has(row.order.id),
            },
          ]
        : [],
    ),
    ...(sequence?.doctorFeeList ?? []).map((item) => ({
      key: `df_${item.id}`,
      category: "df" as const,
      status: FEE_STATUS,
      items: [item.doctor_fee_rule_display ?? ""],
      isCancelled: false,
      isEditing: editingFeeIds.has(Number(item.id)),
      isLocked: false,
      canEdit: !!item.id,
      canCancel: !!item.id,
      df: item,
    })),
    ...(sequence?.miscList ?? []).map((item) => ({
      key: `misc_${item.id}`,
      category: "misc" as const,
      status: FEE_STATUS,
      items: [[item.name, item.note].filter(Boolean).join(" · ")],
      isCancelled: false,
      isEditing: false,
      isLocked: false,
      canEdit: false,
      canCancel: !!item.id,
      misc: item,
    })),
  ];

  const doneKeys = doneRows.map((row) => row.key).join();

  const refreshStatus = props.buttonLoadCheck?.[BTN_ACTS.REFRESH];

  useEffect(() => {
    const base = confirmRef.current;

    if (!base || refreshStatus !== BLC_STATUS.SUCCESS) {
      return;
    }

    const created = doneRows.filter((row) => !base.keys.includes(row.key)).length;

    setFlash(
      [
        `ยืนยันคำสั่งแล้ว · สร้าง Order ใหม่ ${created} รายการ · แก้ไข Order ${base.edited} รายการ`,
        base.cancelled > 0 ? `ยกเลิก Order ${base.cancelled} รายการ (ไม่มีรายการเหลือ)` : "",
        base.pkgCount > 0
          ? `ส่ง ${base.pkgCount} รายการใช้สิทธิ์แพ็กเกจไปที่สรุปค่าใช้จ่ายแล้ว (การเงินตัดสิทธิ์ตอนชำระ)`
          : "",
      ]
        .filter(Boolean)
        .join(" · "),
    );
  }, [refreshStatus, doneKeys]);

  const doneGroups = SHEET_CATEGORIES.map((category) => {
    const rows = doneRows.filter((row) => row.category === category);

    return {
      category,
      rows,
      cancelled: rows.filter((row) => row.isCancelled).length,
      total: rows
        .filter((row) => !row.isCancelled)
        .reduce((sum, row) => sum + Number(priceOf(row) || 0), 0),
    };
  }).filter((group) => group.rows.length > 0);

  // แถบสรุป: คำสั่งที่ยืนยันแล้ว ไม่นับที่ยกเลิกและกำลังแก้ไข
  const confirmedGroups = doneGroups
    .map((group) => ({
      category: group.category,
      rows: group.rows.filter((row) => !row.isCancelled && !row.isEditing),
    }))
    .filter((group) => group.rows.length > 0);
  const confirmedCount = confirmedGroups.reduce((sum, group) => sum + group.rows.length, 0);
  const confirmedTotal = confirmedGroups.reduce(
    (sum, group) => sum + group.rows.reduce((rowSum, row) => rowSum + Number(priceOf(row) || 0), 0),
    0,
  );
  const summaryText = [
    confirmedCount > 0 ? `ยืนยันแล้ว ${confirmedCount} รายการ` : "",
    confirmedCount > 0 ? `≈ ${formatComma(confirmedTotal, "en-US", false)} ฿` : "",
    pendingCount > 0 ? `รอยืนยัน ${pendingCount} รายการ` : "",
  ]
    .filter(Boolean)
    .join(" · ");

  const orderLog = sequence?.orderLog;

  const logRows =
    orderLog && logRow?.order?.id === orderLog.orderId
      ? getLogRows(orderLog.kind, orderLog.items)
      : [];

  const error = props.errorMessage?.[SEQUENCE_NAME];

  const labError = props.errorMessage?.[LAB_CARD_KEY];

  const isSaving =
    props.buttonLoadCheck?.[`${LAB_CARD_KEY}_save`] === BLC_STATUS.LOADING ||
    props.buttonLoadCheck?.[`${LAB_CARD_KEY}_edit_save`] === BLC_STATUS.LOADING ||
    props.buttonLoadCheck?.[SHEET_BTN_ACTS.CONFIRM] === BLC_STATUS.LOADING;

  const isSavingSet = props.buttonLoadCheck?.[SHEET_BTN_ACTS.SAVE_SET] === BLC_STATUS.LOADING;

  const queryLength = query.trim().length;

  const hasQuery = queryLength >= MIN_QUERY;

  const isSheetSearch = searchTarget === "sheet";

  const isSearching =
    props.buttonLoadCheck?.[`${LAB_CARD_KEY}_search`] === BLC_STATUS.LOADING ||
    props.buttonLoadCheck?.[SHEET_BTN_ACTS.SEARCH] === BLC_STATUS.LOADING;

  const renderPickerInput = (label: string, placeholder: string) => (
    <input
      className="hv4-input"
      type="search"
      aria-label={label}
      placeholder={placeholder}
      value={query}
      onChange={(e) => handleChangeQuery(e.target.value)}
    />
  );

  const renderPickerResults = (options: PickItem[], onPick: (key: string) => void) => (
    <>
      {queryLength > 0 && !hasQuery && (
        <div className="hv4-ord-results">
          <div className="hv4-ord-results-more">พิมพ์อย่างน้อย {MIN_QUERY} ตัวอักษรเพื่อค้นหา</div>
        </div>
      )}
      {hasQuery && (
        <div className="hv4-ord-results">
          {options.slice(0, MAX_RESULTS).map((pick) => (
            <div key={pick.key} className="hv4-ord-result">
              <button type="button" className="hv4-ord-result-btn" onClick={() => onPick(pick.key)}>
                <ChipV4 size="sm" tone={CATEGORY_TONES[pick.category]}>
                  {CATEGORY_LABELS[pick.category]}
                </ChipV4>
                <b className="hv4-ord-result-name">{pick.name}</b>
                <span className="hv4-ord-result-price">{formatPickPrice(pick)}</span>
              </button>
            </div>
          ))}
          {options.length === 0 && !isSearching && (
            <div className="hv4-ord-results-empty">ไม่พบรายการ ลองเปลี่ยนคำค้น</div>
          )}
          {options.length > MAX_RESULTS && (
            <div className="hv4-ord-results-more">
              แสดง {MAX_RESULTS} จาก {options.length} รายการ · พิมพ์ชื่อเพิ่มเพื่อกรองให้แคบลง
            </div>
          )}
        </div>
      )}
    </>
  );

  const labResults: PickItem[] =
    hasQuery && ["all", "lab"].includes(categoryFilter)
      ? (props.OrderCentralLabSequence?.labTests ?? []).map((test: LabTest) => ({
          key: `lab_${test.lab_code}`,
          category: "lab",
          name: test.name,
          price: test.price ?? null,
          lab: test,
        }))
      : [];

  const sheetResults: PickItem[] = hasQuery
    ? (props.OrderSheetV4Sequence?.results ?? [])
        .filter((item) => [item.kind, "all"].includes(categoryFilter))
        .map((item) => ({
          key: item.key,
          category: item.kind,
          name: item.name,
          price: item.price,
          item,
        }))
    : [];

  const searchResults = [...labResults, ...sheetResults];

  const searchError = props.OrderSheetV4Sequence?.searchError;

  const noticeText = props.OrderCentralLabSequence?.duplicateLabDetected
    ? (errorTextOf(props.OrderCentralLabSequence.promptMessage) ?? "เกิดข้อผิดพลาด")
    : (notice?.text ?? props.OrderSheetV4Sequence?.groupNotice);

  const orderSets: Record<string, any>[] = props.OrderSetTemplateSequence?.templateList ?? [];

  const setRows = orderSets.filter((item) =>
    setTab === "mine" ? item.type === "P" : setTab === "clinic" ? item.type !== "P" : true,
  );

  const setEntries = (id: number): SetEntry[] =>
    (props.OrderSheetV4Sequence?.sets?.[id] ?? []).filter(
      (entry) => !setRemoved[id]?.includes(entry.key),
    );

  const favKeys = new Set(favourites.map((item) => item.key));

  const favOptions = searchResults.filter((item) => !favKeys.has(item.key));

  const hasDraft = draftItems.length > 0 || sheetLines.length > 0 || sheetEditing.length > 0;

  const incomplete = groups.flatMap((group) =>
    group.lines.filter((line) => line.missing?.length).map((line) => line.name.split(" ")[0]),
  );

  const adrPending = sheetLines
    .filter((line) => line.raw.adr && !line.fields.adrNote)
    .map((line) => line.name.split(" ")[0]);

  const missing = [
    invalidDF ? "เลือกแพทย์ผู้รับ DF ที่เป็นผู้ให้บริการของ OA นี้" : "",
    pendingCount === 0 && sheetEditing.length === 0 ? "เพิ่มรายการอย่างน้อย 1 รายการ" : "",
    incomplete.length ? `กรอกรายละเอียดที่ต้องระบุ: ${incomplete.join(", ")}` : "",
    adrPending.length ? `ติ๊กรับทราบประวัติแพ้ยา: ${adrPending.join(", ")}` : "",
  ].filter(Boolean);

  const isConfirmOff = missing.length > 0 || !hasDraft || isSaving;

  const missText =
    !hasDraft && lastConfirmed
      ? "ยืนยันคำสั่งแล้ว · เพิ่มรายการใหม่เพื่อยืนยันเพิ่มได้"
      : missing.length
        ? `ยังไม่พร้อมยืนยัน: ${missing.join(" · ")}`
        : "พร้อมยืนยันคำสั่ง";

  const adrAlert = props.OrderSheetV4Sequence?.adrAlert ?? null;

  const adrOrderable = (adrAlert?.warnings ?? []).filter((warning) => !warning.locked);

  const adrLocked = (adrAlert?.warnings ?? []).filter((warning) => warning.locked);

  const interactionAlert = props.OrderSheetV4Sequence?.interactionAlert ?? null;

  const reviewItems = getReviewItems(sheetLines);

  const adrOf = (pick: PickItem) =>
    pick.item?.kind === "drug" && pick.item.id
      ? props.OrderSheetV4Sequence?.adrResults?.[pick.item.id]
      : undefined;

  const packageOf = (pick: PickItem) => {
    const product = pick.lab ? Number(pick.lab.product_id) : pick.item?.id;

    return packages.find((item) => item.product === product);
  };

  // Handler
  const handleToggleGroup = (category: SheetCategory) => {
    setOpenGroups({ ...openGroups, [category]: openGroups[category] === false });
  };

  const handleEditOrder = (row: DoneRow) => {
    const order = row.order;
    const kind = row.category as EditableKind | "lab";

    if (!order && !row.df) {
      return;
    }

    confirmRef.current = null;
    setFlash(`ดึง ${codeOf(row)} กลับมาแก้ไขในรายการรอยืนยันแล้ว · แก้เสร็จกดยืนยันคำสั่งอีกครั้ง`);
    setOpenGroups({ ...openGroups, [kind]: true });

    if (row.df) {
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.LOAD_ORDER,
        kind: "df",
        orderId: Number(row.df.id),
        fee: row.df,
      });

      return;
    }

    if (!order) {
      return;
    }

    const prices: any[] = order.price?.orders ?? [];

    if (kind === "lab") {
      handleCloseError(LAB_CARD_KEY);
      props.runSequence({ sequence: LAB_SEQUENCE, action: "edit", selected: order.id });

      return;
    }

    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.LOAD_ORDER,
      kind,
      orderId: order.id,
      prices: prices.map((item) => Number(item.price_unit || 0)),
      specificType: order.specific_type_code,
    });
  };

  const handleOpenCancel = (row: DoneRow) => {
    setCancelRow(row);
    setCancelNote("");
    setCancelPassword("");
  };

  const handleOpenLog = (row: DoneRow) => {
    setLogRow(row);

    if (row.order?.id !== undefined && isLogKind(row.category)) {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: ACTIONS.LOAD_LOG,
        orderId: row.order.id,
        kind: row.category,
      });
    }
  };

  const handleCloseLog = () => {
    handleCloseError(BTN_ACTS.LOAD_LOG);
    setLogRow(null);
  };

  const handleCloseCancel = () => {
    if (cancelRow) {
      handleCloseError(cancelKeyOf(cancelRow));
    }

    setCancelRow(null);
  };

  const handleConfirmCancel = () => {
    if (!cancelRow) {
      return;
    }

    const fee = cancelRow.df ?? cancelRow.misc;

    if (fee?.id !== undefined) {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: ACTIONS.CANCEL_FEE,
        btnAction: cancelKeyOf(cancelRow),
        kind: cancelRow.df ? "df" : "misc",
        id: fee.id,
        note: cancelNote,
        password: cancelPassword,
        onSuccess: () => handleCancelSuccess(cancelRow),
      });
    } else if (cancelRow.order?.id !== undefined) {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: ACTIONS.CANCEL,
        btnAction: cancelKeyOf(cancelRow),
        id: cancelRow.order.id,
        note: cancelNote,
        password: cancelPassword,
        onSuccess: () => handleCancelSuccess(cancelRow),
      });
    }
  };

  const handleCancelSuccess = (row: DoneRow) => {
    const orderId = row.order?.id;

    confirmRef.current = null;
    setCancelRow(null);
    setFlash(`ยกเลิก ${codeOf(row)} แล้ว`);

    if (orderId === undefined) {
      return;
    }

    if (row.category !== "lab") {
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.DROP_ORDER,
        kind: row.category as EditableKind,
        orderId,
      });
    } else if (labEditId === orderId) {
      props.setProp?.("OrderCentralLabSequence.labOrder", { order_items: [] });
    }
  };

  const handleCloseError = (key: string) => {
    props.setProp?.(`errorMessage.${key}`, null);
  };

  const handleChangeQuery = (value: string, target: SearchTarget = "sheet") => {
    setSearchTarget(target);
    setQuery(value);
    props.setProp?.("OrderCentralLabSequence.labName", value);
  };

  const handleClearSearch = () => {
    handleChangeQuery("");
    setCategoryFilter("all");
  };

  const handleAddItem = (pick: PickItem) => {
    const lines = groups.find((group) => group.category === pick.category)?.lines ?? [];
    const existing = lines.find((line) => line.name === pick.name);

    handleClearSearch();

    if (existing) {
      setFlash("ข้าม 1 รายการที่มีอยู่แล้ว");
      setNotice({
        text: `มี "${pick.name}" ในใบคำสั่งแล้ว · ไม่เพิ่มซ้ำ`,
        key: existing.key,
        category: pick.category,
      });

      return;
    }

    if (pick.lab) {
      handleAddLabTest(pick.lab);
    } else if (pick.item) {
      setNotice(null);
      props.setProp?.("OrderSheetV4Sequence.errors", {});
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.ADD_ITEMS,
        entries: [
          {
            key: pick.item.key,
            kind: pick.item.kind,
            name: pick.item.name,
            price: Number(pick.item.price || 0),
            quantity: 1,
            item: pick.item,
            fields: pick.fields,
          },
        ],
        isDoctor: props.isDoctor,
      });
    }
  };

  const handleAddLabTest = (test: LabTest) => {
    setLabTestInfo({ ...labTestInfo, [test.lab_code]: test });
    setPendingCode(test.lab_code);
    setFlash("");
    setNotice(null);
    handleCloseError(LAB_CARD_KEY);

    props.runSequence({
      sequence: LAB_SEQUENCE,
      action: "add",
      cardKey: LAB_CARD_KEY,
      addedItem: test,
    });
  };

  const handleGoDuplicate = () => {
    if (!notice?.key) {
      return;
    }

    setOpenGroups({ ...openGroups, [notice.category ?? "lab"]: true });
    document.getElementById(`hv4-ord-line-${notice.key}`)?.scrollIntoView({ block: "center" });
  };

  const handleCloseNotice = () => {
    setNotice(null);
    props.setProp?.("OrderCentralLabSequence.duplicateLabDetected", false);
    props.setProp?.("OrderSheetV4Sequence.groupNotice", null);
  };

  const handleToggleLine = (key: string) => {
    setOpenLines({ ...openLines, [key]: !openLines[key] });
  };

  const handleChangeItem = (index: number, field: "urgency" | "note", value: string) => {
    props.setProp?.(`OrderCentralLabSequence.labOrder.order_items.${index}.${field}`, value);
  };

  const handleDeleteItem = (index: number) => {
    const items = draftItems.filter((_, i) => i !== index);

    props.setProp?.("OrderCentralLabSequence.labOrder.order_items", items);

    if (items.length === 0) {
      props.setProp?.("OrderCentralLabSequence.labOrder", { order_items: [] });
    }

    props.runSequence({ sequence: LAB_SEQUENCE, action: "delete" });
  };

  const handleToggleSet = (id: number) => {
    setOpenSetId(openSetId === id ? null : id);
    setSetExcluded({});
  };

  const handleRemoveSetEntry = (id: number, key: string) => {
    setSetRemoved({ ...setRemoved, [id]: [...(setRemoved[id] ?? []), key] });
  };

  const handleSaveSet = () => {
    const name = saveSet.name.trim();
    const scope = saveSet.scope;

    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.SAVE_SET,
      name,
      scope,
      doctorId: props.doctorId,
      labItems: draftItems,
      onDone: (result: SaveSetResult) => {
        if (result.missing?.length) {
          setNotice({ text: `บันทึก Order set ไม่ได้: ${result.missing.join(" · ")}` });

          return;
        }

        if (result.error) {
          setNotice({
            text: `บันทึก Order set ไม่ได้: ${errorTextOf(result.error) ?? "เกิดข้อผิดพลาด"}`,
          });

          return;
        }

        const skipped = result.skipped ?? [];

        setNotice(null);
        setSaveSet({ open: false, name: "", scope });
        setSetTab(scope);
        setFlash(
          [
            `บันทึก Order set "${name}" (${SET_TABS.find(([key]) => key === scope)?.[1]}) แล้ว · ${result.count} รายการ พร้อมรายละเอียดที่กรอกไว้`,
            skipped.length ? `ไม่ได้เก็บ ${skipped.length} รายการ: ${skipped.join(", ")}` : "",
          ]
            .filter(Boolean)
            .join(" · "),
        );
        props.runSequence({
          sequence: ORDER_SET_SEQUENCE,
          action: "searchTemplate",
          cardKey: ORDER_SET_CARD_KEY,
        });
      },
    });
  };

  const handleAddSet = (id: number, name: string) => {
    addEntries(
      setEntries(id).filter((entry) => !setExcluded[entry.key]),
      `เพิ่มชุด "${name}"`,
    );
  };

  const addEntries = (chosen: SetEntry[], label: string) => {
    const names = new Set<string>();
    const labs: LabTest[] = [];
    const others: SetEntry[] = [];
    let skipped = 0;

    for (const entry of chosen) {
      const category = entry.kind;
      const lines = groups.find((group) => group.category === category)?.lines ?? [];
      const nameKey = `${category}:${entry.name}`;

      if (names.has(nameKey) || lines.some((line) => line.name === entry.name)) {
        skipped++;
        continue;
      }

      names.add(nameKey);

      if (entry.kind === "lab") {
        if (entry.lab) {
          labs.push(entry.lab);
        } else {
          skipped++;
        }
      } else {
        others.push(entry);
      }
    }

    const count = labs.length + others.length;
    const message = [
      count > 0 ? `${label} ${count} รายการเข้าใบคำสั่งแล้ว` : "",
      skipped > 0 ? `ข้าม ${skipped} รายการที่มีอยู่แล้ว` : "",
    ]
      .filter(Boolean)
      .join(" · ");

    setNotice(null);
    setPanel("");
    setOpenSetId(null);
    setSetExcluded({});
    setFlash(message);

    if (labs.length > 0) {
      setLabTestInfo({
        ...labTestInfo,
        ...Object.fromEntries(labs.map((test) => [test.lab_code, test])),
      });
      handleCloseError(LAB_CARD_KEY);
      props.runSequence({ sequence: LAB_SEQUENCE, action: "group_add", items: labs });
    }

    if (others.length <= 0) {
      return;
    }
    setFlashRef.current = message;
    props.setProp?.("OrderSheetV4Sequence.errors", {});
    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.ADD_ITEMS,
      entries: others,
      isDoctor: props.isDoctor,
    });
  };

  const handleResolveAdr = (choice: AdrChoice) => {
    if (!adrAlert) {
      return;
    }

    const excluded = adrAlert.warnings.filter(
      (warning) => choice !== "order" || warning.locked,
    ).length;
    const addCount = adrAlert.entries.length - excluded;

    if (choice === "cancel") {
      setFlash("");
      setFlashRef.current = "";
    } else {
      const base =
        setFlashRef.current || (addCount > 0 ? `เพิ่ม ${addCount} รายการเข้าใบคำสั่งแล้ว` : "");
      const message = [base, excluded > 0 ? `ไม่รวมยาที่แพ้ ${excluded} รายการ` : ""]
        .filter(Boolean)
        .join(" · ");

      setFlash(message);
      setFlashRef.current = addCount > 0 ? message : "";
    }

    setAdrReason("");
    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.RESOLVE_ADR,
      choice,
      reason: adrReason,
    });
  };

  const handleResolveInteraction = (choice: "cancel" | "order") => {
    if (choice === "cancel") {
      setFlash("");
      setFlashRef.current = "";
    }

    setInteractionReason("");
    props.runSequence({
      sequence: SHEET_SEQUENCE,
      action: SHEET_ACTIONS.RESOLVE_INTERACTION,
      choice,
      reason: interactionReason.trim(),
    });
  };

  const handleLoadSets = () => {
    const ids = orderSets.map((item) => item.id);

    if (ids.length > 0) {
      props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.LOAD_SETS, ids });
    }
  };

  const handlePrintAppointment = (appointment: Record<string, any>, language: "TH" | "EN") => {
    const block = appointment.display_info ?? {};

    props.onEvent?.({
      message: "PrintScheduling",
      params: {
        form: "FormAppointmentDetail",
        data: {
          ...appointment,
          date: formatDate(moment(appointment.estimated_at_iso)),
          datetime: `${moment(block.start_datetime_iso).format("HH:mm")}-${moment(block.end_datetime_iso).format("HH:mm")}`,
          doctor_note: appointment.order_note?.match(/^.*[^\n\r]/gm) || "",
          language,
        },
      },
    } as any);
  };

  const handleTogglePanel = (value: Panel) => {
    setPanel(panel === value ? "" : value);
  };

  const handleChangeSheetLine = (
    key: string,
    data: { quantity?: number; priceUnit?: number; fields?: Record<string, string> },
  ) => {
    props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.EDIT, key, ...data });
  };

  const handleSearchIcd9 = (query: string) => {
    clearTimeout(icd9TimerRef.current);

    icd9TimerRef.current = setTimeout(() => {
      props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.SEARCH_ICD9, query });
    }, 400);
  };

  const handleRemoveSheetLine = (key: string) => {
    props.runSequence({ sequence: SHEET_SEQUENCE, action: SHEET_ACTIONS.REMOVE, key });
  };

  const handleToggleFavourite = (pick?: PickItem) => {
    if (!pick) {
      return;
    }

    setFavourites(
      favKeys.has(pick.key)
        ? favourites.filter((item) => item.key !== pick.key)
        : [...favourites, pick],
    );
  };

  const handleAddFavourite = (key: string) => {
    const pick = favOptions.find((item) => item.key === key);

    if (pick) {
      setFavourites([...favourites, pick]);
      handleChangeQuery("");
    }
  };

  const handleAddFromFavourite = (pick: PickItem) => {
    setPanel("");
    handleAddItem(pick);
  };

  const handleConfirm = () => {
    if (invalidDF) return;
    if (reviewItems.length > 0 && !isReviewOpen) {
      setIsReviewOpen(true);

      return;
    }

    setIsReviewOpen(false);

    const cancelled = sheetEditing.filter(
      (item) =>
        !sheetLines.some((line) => line.kind === item.kind && line.orderId === item.orderId),
    ).length;

    confirmRef.current = {
      keys: doneRows.map((row) => row.key),
      edited:
        [...editingOrderIds].filter((id) => id !== undefined).length +
        editingFeeIds.size -
        cancelled,
      cancelled,
      pkgCount: pkgUse.size,
    };

    const refresh = () => {
      setFlash("");
      props.runSequence({ sequence: SEQUENCE_NAME, action: ACTIONS.REFRESH });
    };

    if (draftItems.length > 0) {
      props.runSequence({
        sequence: LAB_SEQUENCE,
        action: labEditId ? "edit_save" : "save",
        cardKey: LAB_CARD_KEY,
        onSuccess: () => {
          setOpenLines({});
          refresh();
        },
      });
    }

    if (sheetLines.length > 0 || sheetEditing.length > 0) {
      props.runSequence({
        sequence: SHEET_SEQUENCE,
        action: SHEET_ACTIONS.CONFIRM,
        kinds: SHEET_KINDS,
        onDone: (saved: SheetKind[]) => {
          if (saved.length > 0) {
            refresh();
          }
        },
      });
    }
  };

  return (
    <div className="hv4-ord">
      {providers.error && (
        <AlertV4 tone="danger">
          {providers.error} <button onClick={providers.reload}>ลองใหม่</button>
        </AlertV4>
      )}
      <div className="hv4-page-head">
        <h1 className="hv4-h1">คำสั่ง</h1>
        <span className="hv4-page-sub">
          Encounter {props.encounterNumber} · {savedText}
        </span>
      </div>

      {!!error && (
        <AlertV4 tone="danger" onClose={() => handleCloseError(SEQUENCE_NAME)}>
          {errorTextOf(error) ?? "เกิดข้อผิดพลาด"}
        </AlertV4>
      )}

      <section aria-label="สรุปคำสั่ง" className="hv4-ord-summary">
        <span className="hv4-ord-summary-label">สรุปคำสั่ง</span>
        {confirmedGroups.map((group) => (
          <ChipV4 key={group.category} size="sm" tone={CATEGORY_TONES[group.category]}>
            {CATEGORY_LABELS[group.category]} {group.rows.length}
          </ChipV4>
        ))}
        <span className="hv4-ord-summary-text">{summaryText || "ยังไม่มีรายการคำสั่ง"}</span>
      </section>

      <CardOrderAppointmentV4
        eligibleProviderIds={eligibleProviderIds}
        OrderAppointmentV4Sequence={props.OrderAppointmentV4Sequence}
        buttonLoadCheck={props.buttonLoadCheck}
        masterOptions={props.masterOptions}
        runSequence={props.runSequence}
        encounterId={Number(props.encounter.id)}
        patient={props.patient}
        divisionName={props.divisionName}
        search={{
          query: searchTarget === "advance" ? query : "",
          category: searchTarget === "advance" ? categoryFilter : "all",
          results: searchTarget === "advance" ? searchResults : [],
          isSearching,
          orderSets: orderSets.map((item) => ({ id: item.id, name: item.name })),
          favourites,
          setEntriesOf: setEntries,
          onChangeQuery: (value) => handleChangeQuery(value, "advance"),
          onChangeCategory: (value) => {
            setSearchTarget("advance");
            setCategoryFilter(value);
          },
          onOpenSets: handleLoadSets,
        }}
        onPrint={handlePrintAppointment}
        onGoAppointments={props.onGoAppointments}
        onChanged={props.onAppointmentChanged}
      />

      {doneGroups.length > 0 && (
        <section aria-label="คำสั่งที่ยืนยันแล้ว" className="hv4-ord-done">
          <h2 className="hv4-ord-done-title">คำสั่งที่ยืนยันแล้ว</h2>
          <div className="hv4-ord-done-wrap">
            <table className="hisv4-table hv4-ord-done-table">
              <thead>
                <tr>
                  <th>รหัส Order</th>
                  <th>Order status</th>
                  <th>รายละเอียด</th>
                  <th className="hv4-ord-done-num">ราคา</th>
                  <th className="hv4-ord-done-act">จัดการ</th>
                </tr>
              </thead>
              <tbody>
                {doneGroups.map((group) => (
                  <React.Fragment key={group.category}>
                    <tr className="hv4-ord-done-group">
                      <td colSpan={5}>
                        <div className="hv4-ord-done-group-line">
                          <ChipV4 size="sm" tone={CATEGORY_TONES[group.category]}>
                            {CATEGORY_LABELS[group.category]}
                          </ChipV4>
                          <span className="hv4-ord-done-count">
                            {group.rows.length} Order
                            {group.cancelled > 0 ? ` (ยกเลิก ${group.cancelled})` : ""}
                          </span>
                          <b className="hv4-ord-done-sum">
                            รวม {formatComma(group.total, "en-US", false)} ฿
                          </b>
                        </div>
                      </td>
                    </tr>
                    {group.rows.map((row) => (
                      <tr
                        key={row.key}
                        className={
                          row.isCancelled
                            ? "hv4-ord-done-cancelled"
                            : row.isEditing
                              ? "hv4-ord-done-editing"
                              : undefined
                        }
                      >
                        <td>
                          <b className="hv4-ord-id">{codeOf(row)}</b>
                          {!!createdTimeOf(row) && (
                            <div className="hv4-ord-id-at">สร้าง {createdTimeOf(row)}</div>
                          )}
                          {row.isEditing && <div className="hv4-ord-done-editchip">กำลังแก้ไข</div>}
                        </td>
                        <td>
                          <span
                            className={`hv4-ord-done-pill${row.isCancelled ? " hv4-ord-done-pill--cancel" : ""}`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td>
                          <ul className="hv4-ord-done-items">
                            {row.items.map((item, index) => (
                              <li key={index}>{item}</li>
                            ))}
                          </ul>
                        </td>
                        <td className="hv4-ord-done-num">
                          <b>{formatComma(priceOf(row) ?? 0, "en-US", false)} ฿</b>
                        </td>
                        <td className="hv4-ord-done-act">
                          <div className="hv4-ord-done-btns">
                            {!row.isCancelled && (
                              <>
                                {row.isLocked ? (
                                  <span
                                    title="ปิดแล้ว แก้ไขไม่ได้"
                                    aria-label="ปิดแล้ว"
                                    className="hv4-ord-lock"
                                  >
                                    🔒
                                  </span>
                                ) : (
                                  <>
                                    {row.canEdit && !row.isEditing && (
                                      <ButtonV4
                                        variant="ghost"
                                        size="icon"
                                        aria-label={`แก้ไข ${codeOf(row)}`}
                                        title="แก้ไข"
                                        onClick={() => handleEditOrder(row)}
                                      >
                                        <Pencil size={18} aria-hidden="true" />
                                      </ButtonV4>
                                    )}
                                    <ButtonV4
                                      variant="danger-ghost"
                                      size="icon"
                                      aria-label={`ยกเลิก ${codeOf(row)}`}
                                      title="ยกเลิก"
                                      disabled={!row.canCancel}
                                      onClick={() => handleOpenCancel(row)}
                                    >
                                      <Trash2 size={18} aria-hidden="true" />
                                    </ButtonV4>
                                  </>
                                )}
                              </>
                            )}
                            <ButtonV4
                              variant="ghost"
                              size="sm"
                              aria-label={`Log ${codeOf(row)}`}
                              title={
                                row.order && isLogKind(row.category)
                                  ? "Log การสร้าง / แก้ไข Order"
                                  : "ยังไม่มี Log ของประเภทนี้"
                              }
                              disabled={!row.order || !isLogKind(row.category)}
                              onClick={() => handleOpenLog(row)}
                            >
                              <History size={14} aria-hidden="true" /> Log
                            </ButtonV4>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {!!logRow && (
        <ModalV4 title="Log การสร้าง / แก้ไข Order" width={820} onClose={handleCloseLog}>
          <div className="hv4-ord-log-body">
            <div className="hv4-ord-cancel-sub">
              {[
                codeOf(logRow),
                CATEGORY_LABELS[logRow.category],
                createdTimeOf(logRow) ? `สร้าง ${createdTimeOf(logRow)}` : "",
                logRow.isCancelled ? "ยกเลิกแล้ว" : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
            {!!props.errorMessage?.[BTN_ACTS.LOAD_LOG] && (
              <AlertV4 tone="danger">
                โหลด Log ไม่สำเร็จ:{" "}
                {errorTextOf(props.errorMessage[BTN_ACTS.LOAD_LOG]) ?? "เกิดข้อผิดพลาด"}
              </AlertV4>
            )}
            {props.buttonLoadCheck?.[BTN_ACTS.LOAD_LOG] === BLC_STATUS.LOADING ? (
              <div role="status" className="hv4-ord-cancel-sub">
                กำลังโหลด Log…
              </div>
            ) : (
              <table className="hisv4-table hv4-ord-log-table">
                <thead>
                  <tr>
                    <th>เวลา</th>
                    <th>Status</th>
                    <th>การดำเนินการ</th>
                    <th>ผู้ดำเนินการ</th>
                  </tr>
                </thead>
                <tbody>
                  {logRows.map((row, index) => (
                    <tr key={index}>
                      <td>{formatISOtoBEDateTime(row.datetime).timeShort}</td>
                      <td>
                        <ChipV4
                          size="sm"
                          bold
                          tone={row.status === "Cancelled" ? "danger-solid" : "blue-solid"}
                        >
                          {row.status}
                        </ChipV4>
                      </td>
                      <td>
                        <b>{row.text}</b>
                      </td>
                      <td>
                        {("name" in row && row.name) || ("user_name" in row && row.user_name)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </ModalV4>
      )}

      {!!cancelRow && (
        <ModalV4
          title="ยืนยันการยกเลิก Order"
          width={520}
          tone="danger"
          onClose={handleCloseCancel}
        >
          <div className="hv4-ord-cancel-body">
            <div className="hv4-ord-cancel-sub">
              {[
                codeOf(cancelRow),
                CATEGORY_LABELS[cancelRow.category],
                cancelRow.items.join(", "),
              ].join(" · ")}
            </div>
            <FieldV4 label="เหตุผลการยกเลิก" htmlFor="hv4_ord_cancel_note">
              <input
                id="hv4_ord_cancel_note"
                className="hv4-input"
                placeholder="ไม่บังคับ เช่น สั่งผิดรายการ"
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
              />
            </FieldV4>
            <FieldV4 label="Password *" htmlFor="hv4_ord_cancel_password">
              <input
                id="hv4_ord_cancel_password"
                type="password"
                className="hv4-input"
                autoComplete="current-password"
                placeholder="ใส่ Password เพื่อยืนยันการยกเลิก"
                value={cancelPassword}
                onChange={(e) => setCancelPassword(e.target.value)}
              />
            </FieldV4>
            {!!props.errorMessage?.[cancelKeyOf(cancelRow)] && (
              <AlertV4 tone="danger">
                {errorTextOf(props.errorMessage[cancelKeyOf(cancelRow)]) ?? "เกิดข้อผิดพลาด"}
              </AlertV4>
            )}
            <div className="hv4-ord-cancel-foot">
              <ButtonV4 variant="ghost" onClick={handleCloseCancel}>
                ไม่ยกเลิก
              </ButtonV4>
              <ButtonV4
                variant="danger"
                disabled={props.buttonLoadCheck?.[cancelKeyOf(cancelRow)] === BLC_STATUS.LOADING}
                onClick={handleConfirmCancel}
              >
                บันทึกการยกเลิก
              </ButtonV4>
            </div>
          </div>
        </ModalV4>
      )}

      {!!flash && <AlertV4 tone="info">{flash}</AlertV4>}

      {!!noticeText && (
        <AlertV4
          tone="warn"
          actions={
            !!notice?.key &&
            !props.OrderCentralLabSequence?.duplicateLabDetected && (
              <ButtonV4 variant="ghost" size="sm" onClick={handleGoDuplicate}>
                ไปที่รายการเดิม
              </ButtonV4>
            )
          }
          onClose={handleCloseNotice}
        >
          ⚠ {noticeText}
        </AlertV4>
      )}

      {!!labError && (
        <AlertV4 tone="danger" onClose={() => handleCloseError(LAB_CARD_KEY)}>
          {errorTextOf(labError) ?? "เกิดข้อผิดพลาด"}
        </AlertV4>
      )}

      {Object.entries(sheetErrors).map(([kind, sheetError]) => (
        <AlertV4
          key={kind}
          tone="danger"
          onClose={() => props.setProp?.(`OrderSheetV4Sequence.errors.${kind}`, undefined)}
        >
          บันทึก {CATEGORY_LABELS[kind as SheetKind]} ไม่สำเร็จ:{" "}
          {errorTextOf(sheetError) ?? "เกิดข้อผิดพลาด"}
        </AlertV4>
      ))}

      <section className="hv4-card hv4-ord-search">
        <div className="hv4-ord-search-row">
          <FieldV4
            className="hv4-ord-search-query"
            label="ค้นหารายการสั่งรายตัว"
            htmlFor="hv4_ord_q"
          >
            <input
              id="hv4_ord_q"
              className="hv4-input"
              placeholder="พิมพ์ชื่อแลป ยา หัตถการ ฯลฯ"
              value={isSheetSearch ? query : ""}
              onChange={(e) => handleChangeQuery(e.target.value)}
            />
          </FieldV4>
          <FieldV4 className="hv4-ord-search-cat" label="หมวด" htmlFor="hv4_ord_c">
            <SelectV4
              id="hv4_ord_c"
              options={[
                { value: "all", text: "ทุกหมวด" },
                ...SHEET_CATEGORIES.map((category) => ({
                  value: category,
                  text: CATEGORY_LABELS[category],
                })),
              ]}
              value={isSheetSearch ? categoryFilter : "all"}
              onChange={(value) => {
                setSearchTarget("sheet");
                setCategoryFilter(value as SheetCategory | "all");
              }}
            />
          </FieldV4>
          <ButtonV4
            variant={panel === "sets" ? "primary" : "ghost"}
            aria-expanded={panel === "sets"}
            onClick={() => handleTogglePanel("sets")}
          >
            Order Set <b>{orderSets.length}</b>
          </ButtonV4>
          <ButtonV4
            variant={panel === "favs" ? "primary" : "ghost"}
            aria-expanded={panel === "favs"}
            onClick={() => handleTogglePanel("favs")}
          >
            ★ รายการโปรด <b>{favourites.length}</b>
          </ButtonV4>
        </div>

        {isSheetSearch && queryLength > 0 && !hasQuery && (
          <div className="hv4-ord-results">
            <div className="hv4-ord-results-more">
              พิมพ์อย่างน้อย {MIN_QUERY} ตัวอักษรเพื่อค้นหา
            </div>
          </div>
        )}

        {isSheetSearch && hasQuery && (
          <div className="hv4-ord-results">
            {searchResults.slice(0, MAX_RESULTS).map((pick) => (
              <div key={pick.key} className="hv4-ord-result">
                <button
                  type="button"
                  className="hv4-ord-result-btn"
                  onClick={() => handleAddItem(pick)}
                >
                  <ChipV4 size="sm" tone={CATEGORY_TONES[pick.category]}>
                    {CATEGORY_LABELS[pick.category]}
                  </ChipV4>
                  {pick.category === "drug" && (
                    <DrugIcon
                      isCapsule={isCapsuleDrug({ stock_unit_name: pick.item?.raw.unit_name })}
                    />
                  )}
                  <b className="hv4-ord-result-name">{pick.name}</b>
                  {!!adrOf(pick) && (
                    <span className="hv4-ord-result-adr">
                      เตือน: แพ้ {adrOf(pick)?.adversary}
                      {adrOf(pick)?.locked ? " · ห้ามสั่ง" : ""}
                    </span>
                  )}
                  {!!packageOf(pick) && (
                    <span className="hv4-ord-result-pkg">
                      📦 {packageOf(pick)?.packageName} · คงเหลือ {packageOf(pick)?.left}
                    </span>
                  )}
                  <span className="hv4-ord-result-price">{formatPickPrice(pick)}</span>
                </button>
                <StarButton
                  name={pick.name}
                  isOn={favKeys.has(pick.key)}
                  onClick={() => handleToggleFavourite(pick)}
                />
              </div>
            ))}
            {!!searchError && !isSearching && (
              <AlertV4 tone="danger">
                ค้นหาไม่สำเร็จ: {errorTextOf(searchError) ?? "เกิดข้อผิดพลาด"}
              </AlertV4>
            )}
            {searchResults.length === 0 && !searchError && !isSearching && (
              <div className="hv4-ord-results-empty">ไม่พบรายการ ลองเปลี่ยนคำค้นหรือหมวด</div>
            )}
            {searchResults.length > MAX_RESULTS && (
              <div className="hv4-ord-results-more">
                แสดง {MAX_RESULTS} จาก {searchResults.length} รายการ ·
                พิมพ์ชื่อเพิ่มเพื่อกรองให้แคบลง
              </div>
            )}
          </div>
        )}
      </section>

      {!!adrAlert && (
        <div className="hv4-modal hv4-modal-alert">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-label="แจ้งเตือนแพ้ยา"
            className="hv4-card hv4-modal-card hv4-ord-adr"
          >
            <h2 className="hv4-card-title hv4-ord-adr-title">⛔ แจ้งเตือน: ผู้ป่วยแพ้ยากลุ่มนี้</h2>
            <p className="hv4-ord-adr-names">
              {adrAlert.warnings.map((warning) => warning.name).join(", ")}
            </p>
            {adrAlert.warnings.map((warning) => (
              <p key={warning.product} className="hv4-ord-adr-notes">
                {warning.notes}
              </p>
            ))}
            {adrLocked.length > 0 && (
              <p className="hv4-ord-adr-locked">
                ห้ามสั่ง: {adrLocked.map((warning) => warning.name).join(", ")}
              </p>
            )}
            {adrOrderable.length > 0 && (
              <FieldV4
                label="เหตุผลที่ยังต้องสั่ง (บังคับเลือก · บันทึกไว้ในใบคำสั่ง)"
                htmlFor="hv4_ord_adr_reason"
              >
                <SelectV4
                  id="hv4_ord_adr_reason"
                  search
                  allowAdd
                  placeholder="เลือกหรือพิมพ์เหตุผล"
                  options={props.masterOptions?.drugAllergyReason ?? []}
                  value={adrReason}
                  onChange={setAdrReason}
                />
              </FieldV4>
            )}
            <div className="hv4-ord-adr-actions">
              <ButtonV4 variant="ghost" onClick={() => handleResolveAdr("cancel")}>
                ยกเลิก · ไม่สั่งยานี้
              </ButtonV4>
              {adrAlert.entries.length > 1 && (
                <ButtonV4 variant="primary" onClick={() => handleResolveAdr("skip")}>
                  เพิ่มโดยไม่รวมยาที่แพ้
                </ButtonV4>
              )}
              {adrOrderable.length > 0 && (
                <ButtonV4
                  className="hv4-ord-adr-ok"
                  disabled={!adrReason}
                  onClick={() => handleResolveAdr("order")}
                >
                  ยืนยันสั่งต่อ
                </ButtonV4>
              )}
            </div>
          </section>
        </div>
      )}

      {!!interactionAlert && (
        <div className="hv4-modal hv4-modal-alert">
          <section
            role="alertdialog"
            aria-modal="true"
            aria-label="แจ้งเตือนยาตีกัน"
            className="hv4-card hv4-modal-card hv4-ord-adr hv4-ord-di"
          >
            <h2 className="hv4-card-title hv4-ord-adr-title">⚠️ แจ้งเตือน: ยาตีกัน</h2>
            <div className="hv4-ord-di-list">
              {interactionAlert.drugs.map((group) => (
                <div key={group.id} className="hv4-ord-di-item">
                  <div className="hv4-ord-di-names">{group.names.join(" + ")}</div>
                  <p>ความรุนแรง: {SEVERITY_TEXT[group.severity ?? 5]}</p>
                  {!!group.affectSummary && <p>ผลของการเกิดอันตรกิริยา: {group.affectSummary}</p>}
                  {!!group.management && <p>การจัดการ: {group.management}</p>}
                </div>
              ))}
              {interactionAlert.labs.map((lab) => (
                <div key={lab.product} className="hv4-ord-di-item">
                  <div className="hv4-ord-di-names">{lab.name}</div>
                  {!!lab.summary && <p>{lab.summary}</p>}
                  {!!lab.detail && <p>{lab.detail}</p>}
                </div>
              ))}
            </div>
            <FieldV4
              label="เหตุผลที่ยังต้องสั่ง (บังคับกรอก · บันทึกไว้ในใบคำสั่ง)"
              htmlFor="hv4_ord_di_reason"
            >
              <SelectV4
                id="hv4_ord_di_reason"
                search
                allowAdd
                placeholder="เลือกหรือพิมพ์เหตุผล"
                options={props.masterOptions?.drugInteractReason ?? []}
                value={interactionReason}
                onChange={setInteractionReason}
              />
            </FieldV4>
            <div className="hv4-ord-adr-actions">
              <ButtonV4 variant="ghost" onClick={() => handleResolveInteraction("cancel")}>
                ยกเลิก · ไม่สั่ง
              </ButtonV4>
              <ButtonV4
                className="hv4-ord-adr-ok"
                disabled={!interactionReason.trim()}
                onClick={() => handleResolveInteraction("order")}
              >
                ยืนยันสั่งต่อ
              </ButtonV4>
            </div>
          </section>
        </div>
      )}

      {isReviewOpen && (
        <div className="hv4-modal">
          <section
            role="dialog"
            aria-modal="true"
            aria-label="ตรวจทานก่อนส่งคำสั่ง"
            className="hv4-card hv4-modal-card hv4-ord-review"
          >
            <h2 className="hv4-card-title">ตรวจทานก่อนส่งคำสั่ง</h2>
            <p className="hv4-ord-review-sub">มีรายการที่ควรตรวจสอบอีกครั้ง</p>
            <div className="hv4-ord-review-list">
              {reviewItems.map((item) => (
                <div key={item.text} className="hv4-ord-review-item">
                  <span>{item.icon}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
            <div className="hv4-ord-adr-actions">
              <ButtonV4 variant="ghost" onClick={() => setIsReviewOpen(false)}>
                กลับไปแก้ไข
              </ButtonV4>
              <ButtonV4 variant="confirm" onClick={handleConfirm}>
                ยืนยันส่งคำสั่ง
              </ButtonV4>
            </div>
          </section>
        </div>
      )}

      {!!panel && (
        <ModalV4
          title={panel === "sets" ? "Order Set" : "★ รายการโปรด"}
          width={800}
          onClose={() => setPanel("")}
        >
          {panel === "sets" && (
            <>
              <div role="tablist" aria-label="ประเภทชุด" className="hv4-ord-set-tabs">
                {SET_TABS.map(([value, label]) => (
                  <ButtonV4
                    key={value}
                    variant="ghost"
                    pill
                    selected={setTab === value}
                    role="tab"
                    aria-selected={setTab === value}
                    onClick={() => setSetTab(value)}
                  >
                    {label}
                  </ButtonV4>
                ))}
              </div>
              <div className="hv4-ord-set-list">
                {setRows.map((item) => {
                  const entries = setEntries(item.id);
                  const categories = [...new Set(entries.map((entry) => entry.kind))];
                  const chosen = entries.filter((entry) => !setExcluded[entry.key]);
                  const isOpen = openSetId === item.id;
                  const total = entries.reduce((sum, entry) => sum + entry.price, 0);

                  return (
                    <div
                      key={item.id}
                      className={`hv4-ord-set${isOpen ? " hv4-ord-set-open" : ""}`}
                    >
                      <div className="hv4-ord-set-row">
                        <div className="hv4-ord-set-info">
                          <div className="hv4-ord-set-title">
                            <b className="hv4-ord-set-name">{item.name}</b>
                            <ChipV4 size="sm" bold tone={item.type === "P" ? "expire" : "grey"}>
                              {item.type === "P" ? "ของฉัน" : "ของคลินิก"}
                            </ChipV4>
                          </div>
                          <div className="hv4-ord-set-meta">
                            {categories.map((category) => (
                              <ChipV4 key={category} size="sm" tone={CATEGORY_TONES[category]}>
                                {CATEGORY_LABELS[category]}
                              </ChipV4>
                            ))}
                            <span>
                              {entries.length} รายการ · {formatComma(total, "en-US", false)} ฿
                            </span>
                          </div>
                        </div>
                        <ButtonV4
                          variant={isOpen ? "ghost" : "primary"}
                          aria-expanded={isOpen}
                          onClick={() => handleToggleSet(item.id)}
                        >
                          {isOpen ? "ปิด" : "เลือกรายการในชุด"}
                        </ButtonV4>
                      </div>
                      {isOpen && (
                        <div className="hv4-ord-set-items">
                          {entries.map((entry) => (
                            <div key={entry.key} className="hv4-ord-set-item">
                              <label className="hv4-ord-set-check">
                                <input
                                  type="checkbox"
                                  checked={!setExcluded[entry.key]}
                                  onChange={() =>
                                    setSetExcluded({
                                      ...setExcluded,
                                      [entry.key]: !setExcluded[entry.key],
                                    })
                                  }
                                />
                                <ChipV4 size="sm" tone={CATEGORY_TONES[entry.kind]}>
                                  {CATEGORY_LABELS[entry.kind]}
                                </ChipV4>
                                <span className="hv4-ord-set-item-name">{entry.name}</span>
                                <span className="hv4-ord-set-item-price">
                                  {formatComma(entry.price, "en-US", false)} ฿
                                </span>
                              </label>
                              <ButtonV4
                                variant="plain"
                                size="icon"
                                className="hv4-ord-set-remove"
                                aria-label={`ลบ ${entry.name} ออกจากชุด`}
                                title={`ลบ ${entry.name} ออกจากชุด`}
                                onClick={() => handleRemoveSetEntry(item.id, entry.key)}
                              >
                                <Trash2 size={18} aria-hidden="true" />
                              </ButtonV4>
                            </div>
                          ))}
                          <div className="hv4-ord-set-foot">
                            <span className="hv4-ord-set-pick">
                              เลือก {chosen.length} จาก {entries.length} รายการ
                            </span>
                            <ButtonV4
                              variant="ok"
                              disabled={chosen.length === 0}
                              onClick={() => handleAddSet(item.id, item.name)}
                            >
                              เพิ่มเข้าใบคำสั่ง
                            </ButtonV4>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {setRows.length === 0 && (
                  <div className="hv4-ord-panel-empty">ยังไม่มีชุดในกลุ่มนี้</div>
                )}
              </div>
            </>
          )}

          {panel === "favs" && (
            <>
              <div className="hv4-ord-fav-list">
                {favourites.map((pick) => (
                  <div key={pick.key} className="hv4-ord-fav-row">
                    <button
                      type="button"
                      className="hv4-ord-fav-add"
                      onClick={() => handleAddFromFavourite(pick)}
                    >
                      <ChipV4 size="sm" tone={CATEGORY_TONES[pick.category]}>
                        {CATEGORY_LABELS[pick.category]}
                      </ChipV4>
                      <b>{pick.name}</b>
                      <span className="hv4-ord-fav-summary">
                        {pick.summary ||
                          (pick.lab ? formatLabSummary({ urgency: "ROUTINE" }, pick.lab) : "")}
                      </span>
                    </button>
                    <ButtonV4
                      variant="plain"
                      size="icon"
                      aria-label={`เอา ${pick.name} ออกจากรายการโปรด`}
                      title="เอาออกจากรายการโปรด"
                      className="hv4-ord-fav-remove"
                      onClick={() => handleToggleFavourite(pick)}
                    >
                      <Trash2 size={18} aria-hidden="true" />
                    </ButtonV4>
                  </div>
                ))}
                {favourites.length === 0 && (
                  <div className="hv4-ord-panel-empty">ยังไม่มีรายการโปรด</div>
                )}
              </div>
              <div className="hv4-ord-picker">
                {renderPickerInput("เพิ่มรายการโปรด", FAV_PLACEHOLDER)}
                {renderPickerResults(favOptions, handleAddFavourite)}
              </div>
            </>
          )}
        </ModalV4>
      )}

      <section className="hv4-ord-sheet">
        <h2 className="hv4-ord-sheet-title">รายการรอยืนยันคำสั่ง</h2>
        <p className="hv4-ord-sheet-desc">
          พักรายการที่เลือกไว้ก่อน · กด "ยืนยันคำสั่ง" แล้วรายการจะย้ายไปอยู่ในส่วน
          คำสั่งที่ยืนยันแล้ว ด้านบน
        </p>

        <div className="hv4-ord-groups">
          {pendingGroups.map((group) => {
            const isOpen = openGroups[group.category] !== false;

            return (
              <div key={group.category} className="hv4-ord-group">
                <button
                  type="button"
                  aria-expanded={isOpen}
                  className="hv4-ord-group-head"
                  onClick={() => handleToggleGroup(group.category)}
                >
                  <span className="hv4-ord-chev">{isOpen ? "▾" : "▸"}</span>
                  <ChipV4 size="sm" tone={CATEGORY_TONES[group.category]}>
                    {CATEGORY_LABELS[group.category]}
                  </ChipV4>
                  <span className="hv4-ord-group-count">{group.lines.length} รายการ</span>
                  {!!group.labText && <span className="hv4-ord-group-lab">{group.labText}</span>}
                  <b className="hv4-ord-group-sub">
                    {formatComma(group.subtotal, "en-US", false)} ฿
                  </b>
                </button>

                {isOpen &&
                  group.lines.map((line) => {
                    const index = line.draftIndex;
                    const item = index === undefined ? undefined : draftItems[index];
                    const test = item ? labTestInfo[item.lab_code] : undefined;
                    const draft = line.draft;
                    const isDraft = index !== undefined || !!draft;
                    const isLineOpen = !!openLines[line.key];
                    const mode = draft ? sheetMode(draft) : null;
                    const pick = pickOfLine(line, test);
                    const kids = test?.children ?? [];
                    const isKidsOpen = kids.length > 0 && !!openKids[line.key];
                    const kidsTitle = `พารามิเตอร์ย่อย ${kids.length} รายการ`;

                    return (
                      <div key={line.key} id={`hv4-ord-line-${line.key}`} className="hv4-ord-line">
                        <div className="hv4-ord-line-row">
                          <div className="hv4-ord-line-main">
                            <div className="hv4-ord-line-name">
                              {draft?.kind === "drug" && (
                                <DrugIcon isCapsule={isCapsuleDrug(draft.raw.detail)} />
                              )}
                              <b>{line.name}</b>
                              {!!line.missing?.length && (
                                <ChipV4 size="sm" bold tone="warn-solid">
                                  ต้องระบุ {line.missing.join(", ")}
                                </ChipV4>
                              )}
                              {!!mode && (
                                <ChipV4 size="sm" bold tone={mode[1]}>
                                  {mode[0]}
                                </ChipV4>
                              )}
                              {line.outLab && (
                                <ChipV4 size="sm" bold tone="purple">
                                  Out lab
                                </ChipV4>
                              )}
                              {kids.length > 0 && (
                                <button
                                  type="button"
                                  className="hv4-ord-kids-btn"
                                  aria-expanded={isKidsOpen}
                                  aria-label={kidsTitle}
                                  title={kidsTitle}
                                  onClick={() =>
                                    setOpenKids({ ...openKids, [line.key]: !isKidsOpen })
                                  }
                                >
                                  ···
                                </button>
                              )}
                            </div>
                            {!!line.summary && (
                              <div className="hv4-ord-line-summary">{line.summary}</div>
                            )}
                            {!!line.pkgText && (
                              <div className="hv4-ord-line-pkg">📦 {line.pkgText}</div>
                            )}
                            {!!line.doseWarning && (
                              <div className="hv4-ord-line-dose">⚠ {line.doseWarning}</div>
                            )}
                          </div>
                          <div className="hv4-ord-line-side">
                            {!!draft && (
                              <SheetLineControls
                                line={draft}
                                onChange={(data) => handleChangeSheetLine(draft.key, data)}
                              />
                            )}
                            <b className="hv4-ord-line-amount">
                              {line.pkgText
                                ? "0 (ใช้สิทธิ์แพ็กเกจ)"
                                : draft && isCostCenter(draft)
                                  ? "0 (เหมารวม)"
                                  : formatComma(line.amount, "en-US", false)}
                            </b>
                            {isDraft && (
                              <>
                                {!!pick && (
                                  <StarButton
                                    name={line.name}
                                    isOn={favKeys.has(pick.key)}
                                    onClick={() => handleToggleFavourite(pick)}
                                  />
                                )}
                                <ButtonV4
                                  variant="ghost"
                                  size="icon"
                                  selected={isLineOpen}
                                  aria-expanded={isLineOpen}
                                  aria-label={`${isLineOpen ? "ปิดรายละเอียด" : "แก้ไข"} ${line.name}`}
                                  title={`${isLineOpen ? "ปิดรายละเอียด" : "แก้ไข"} ${line.name}`}
                                  onClick={() => handleToggleLine(line.key)}
                                >
                                  <Pencil size={18} aria-hidden="true" />
                                </ButtonV4>
                                <ButtonV4
                                  variant="danger-ghost"
                                  size="icon"
                                  aria-label={`ลบ ${line.name}`}
                                  title={`ลบ ${line.name}`}
                                  onClick={() =>
                                    draft
                                      ? handleRemoveSheetLine(draft.key)
                                      : index !== undefined && handleDeleteItem(index)
                                  }
                                >
                                  <Trash2 size={18} aria-hidden="true" />
                                </ButtonV4>
                              </>
                            )}
                          </div>
                        </div>

                        {!!line.adr && (
                          <div className="hv4-ord-line-adr">
                            แพ้ยา ({line.adr.adversary}) · แพทย์ยืนยันสั่งต่อ:{" "}
                            {line.adr.reason || "-"}
                          </div>
                        )}

                        {isKidsOpen && (
                          <div className="hv4-ord-kids" aria-label={kidsTitle}>
                            {kids.map((kid) => (
                              <ChipV4 key={kid.id ?? kid.name} size="sm" tone="purple">
                                {kid.name}
                              </ChipV4>
                            ))}
                          </div>
                        )}

                        {!!draft && isLineOpen && (
                          <SheetLineFields
                            line={draft}
                            doctorOptions={eligibleDoctorOptions}
                            masterOptions={props.masterOptions}
                            icd9Options={props.OrderSheetV4Sequence?.icd9Options ?? []}
                            searchedItemListWithKey={props.searchedItemListWithKey}
                            onEvent={(event) => props.onEvent?.(event)}
                            onChange={(fields) => handleChangeSheetLine(draft.key, { fields })}
                            onSearchIcd9={handleSearchIcd9}
                            onClose={() => handleToggleLine(line.key)}
                          />
                        )}

                        {!!item && index !== undefined && isLineOpen && (
                          <div className="hv4-ord-lab-card">
                            <SelectV4
                              className="hv4-ord-lab-pri"
                              aria-label="ความเร่งด่วน"
                              options={Object.entries(URGENCY_LABELS).map(([value, label]) => ({
                                value,
                                text: label,
                              }))}
                              value={item.urgency}
                              onChange={(value) => handleChangeItem(index, "urgency", value)}
                            />
                            <span>ส่งตรวจ:</span>
                            <SelectV4
                              className="hv4-ord-lab-dest"
                              aria-label="ส่งตรวจ"
                              options={labDestinationOptions(item.lab_type_label)}
                              value={item.lab_type_label === "Out lab" ? "OUT" : "IN"}
                              onChange={() => undefined}
                            />
                            {!!formatTat(test) && (
                              <span className="hv4-ord-lab-tat">TAT ~{formatTat(test)}</span>
                            )}
                            <input
                              className="hv4-input hv4-ord-lab-note"
                              aria-label="หมายเหตุ"
                              placeholder="หมายเหตุ (ข้อมูลทางคลินิก)"
                              value={item.note ?? ""}
                              onChange={(e) => handleChangeItem(index, "note", e.target.value)}
                            />
                            <ButtonV4 variant="primary" onClick={() => handleToggleLine(line.key)}>
                              บันทึก
                            </ButtonV4>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            );
          })}

          {pendingGroups.length === 0 && (
            <div className="hv4-ord-empty">
              {doneRows.length > 0
                ? "ไม่มีรายการรอยืนยัน · รายการที่ยืนยันแล้วอยู่ในส่วน คำสั่งที่ยืนยันแล้ว ด้านบน · เพิ่มรายการใหม่ได้จากช่องค้นหา"
                : "ยังไม่มีรายการ · ค้นหารายการ หรือเลือกจาก Order Set / รายการโปรด / Personalized"}
            </div>
          )}
        </div>

        <div className="hv4-ord-sheet-total">
          <ButtonV4
            variant="ghost"
            aria-expanded={saveSet.open}
            disabled={pendingCount === 0}
            onClick={() => setSaveSet({ ...saveSet, open: !saveSet.open })}
          >
            บันทึกเป็น Order set
          </ButtonV4>
          <span>
            {pendingCount} รายการ · รวม <b>{formatComma(pendingTotal, "en-US", false)}</b> บาท
          </span>
        </div>
        {saveSet.open && pendingCount > 0 && (
          <div className="hv4-ord-save-set">
            <input
              className="hv4-input"
              aria-label="ชื่อ Order set"
              placeholder="ชื่อ Order set"
              value={saveSet.name}
              onChange={(e) => setSaveSet({ ...saveSet, name: e.target.value })}
            />
            <SelectV4
              className="hv4-ord-save-set-scope"
              aria-label="เก็บเป็น"
              options={[
                { value: "mine", text: "ของฉัน" },
                { value: "clinic", text: "ของคลินิก" },
              ]}
              value={saveSet.scope}
              onChange={(value) => setSaveSet({ ...saveSet, scope: value as SaveScope })}
            />
            <ButtonV4
              variant="ok"
              disabled={!saveSet.name.trim() || isSavingSet}
              onClick={handleSaveSet}
            >
              บันทึก
            </ButtonV4>
          </div>
        )}
      </section>

      <div className="hv4-ord-confirm">
        <span className={`hv4-ord-confirm-text${missing.length ? " hv4-ord-confirm-miss" : ""}`}>
          {missText}
        </span>
        <ButtonV4 variant="confirm" disabled={isConfirmOff} onClick={handleConfirm}>
          ยืนยันคำสั่ง
        </ButtonV4>
      </div>
    </div>
  );
};

const DrugIcon = (props: { isCapsule: boolean }) => (
  <span className="hv4-ord-line-icon" aria-hidden="true">
    <svg width="26" height="26" viewBox="0 0 26 26">
      {props.isCapsule ? (
        <g transform="rotate(-45 13 13)" stroke="currentColor" strokeWidth="2">
          <rect className="hv4-ord-pill-body" x="2" y="7" width="22" height="12" rx="6" />
          <path className="hv4-ord-pill-half" d="M13 7h5a6 6 0 0 1 0 12h-5z" />
        </g>
      ) : (
        <g stroke="currentColor" strokeWidth="2">
          <circle className="hv4-ord-pill-body" cx="13" cy="13" r="10" />
          <path d="M5 13h16" />
        </g>
      )}
    </svg>
  </span>
);

type StarButtonProps = {
  name: string;
  isOn: boolean;
  onClick: () => void;
};

const StarButton = (props: StarButtonProps) => {
  const label = `${props.isOn ? "เอาออกจากรายการโปรด" : "เพิ่มเป็นรายการโปรด"} ${props.name}`;

  return (
    <ButtonV4
      variant="plain"
      size="icon"
      aria-pressed={props.isOn}
      aria-label={label}
      title={label}
      className={`hv4-ord-star${props.isOn ? " hv4-ord-star-on" : ""}`}
      onClick={props.onClick}
    >
      {props.isOn ? "★" : "☆"}
    </ButtonV4>
  );
};

CardOrdersV4.displayName = "CardOrdersV4";

export default React.memo(CardOrdersV4);
